import React from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import breakpoint from "styled-components-breakpoint";
import _ from "lodash";
import { findAppDetailByPathname, formatRatingCount } from "./config";

/**
 * 单个 App 的静态详情页，版式参照 App Store 商品页：
 * 图标 + 名称 + 副标题 + 商店关键指标 + 下载入口，往下依次是应用截图、应用简介、功能亮点。
 *
 * 只保留与 App Store 口径一致的内容：商店接口里的更新说明、会员/用户/隐私协议、
 * 评分明细这些上架物料不在官网正文重复展示，避免页面又长又和商店说法对不上。
 * 应用简介与功能亮点直接解析 store-data 里的商店简介，与 App Store 一字不差。
 *
 * 这里刻意不使用 useIsPhone / Rem，断点差异全部交给 CSS 媒体查询，
 * 页面正文在服务端和客户端是同一棵 DOM 树；
 * 静态化时仍按桌面断点出首屏（页头页脚会按断点换 DOM），手机版访问时由客户端重新渲染。
 */

const Stars = ({ value }) => (
  <StarsRow aria-label={`${value} 分，满分 5 分`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} className={i <= Math.round(value) ? "on" : "off"} />
    ))}
  </StarsRow>
);

const MetaItem = ({ label, children }) => (
  <MetaItemWrapper>
    <span className="label">{label}</span>
    <span className="value">{children}</span>
  </MetaItemWrapper>
);

const DownloadButtons = ({ page, store, className }) => (
  <Actions className={className}>
    <a className="primary" href={page.iosLink} rel="noopener noreferrer">
      App Store 下载
    </a>
    {page.androidMarket && (
      <a
        className="secondary"
        href={page.androidMarket}
        rel="noopener noreferrer"
      >
        Android 应用市场
      </a>
    )}
    {/* <span className="price">{_.trim(store.price) || "App内购买"}</span> */}
  </Actions>
);

export default function AppDetail() {
  const { slug } = useParams();
  const page = findAppDetailByPathname(`/app/${slug}`);

  if (!page) {
    return (
      <Page>
        <NotFound>
          <h1>未找到该应用</h1>
          <p>
            这个应用还没有生成静态详情页，
            <Link to="/">返回官网首页</Link>
            查看已上线的应用。
          </p>
        </NotFound>
      </Page>
    );
  }

  const { store, appName, brand, series, features, paragraphs } = page;

  return (
    <Page>
      <Hero>
        <HeroInner>
          {/* <Crumb>
            <ol>
              <li>
                <Link to="/">来音音乐教育官网</Link>
              </li>
              <li>
                <Link to={series ? series.pathname : "/"}>
                  {series ? series.label : brand}
                </Link>
              </li>
              <li aria-current="page">{appName}</li>
            </ol>
          </Crumb> */}

          <HeroMain>
            <img
              className="icon"
              src={page.icon}
              alt={`${appName} App 图标`}
              width="100"
              height="100"
            />
            <div className="head">
              <h1>{appName}</h1>
              {page.subtitle && <p className="subtitle">{page.subtitle}</p>}
              {/* <a
                className="developer"
                href={store.appStoreUrl}
                rel="noopener noreferrer"
              >
                {store.artistName}
              </a> */}
              {/* {_.size(page.awards) > 0 && (
                <ul className="awards">
                  {_.map(page.awards, (item) => (
                    <li key={`${item.name}-${item.device}`}>
                      <span className="name">{item.name}</span>
                      <span className="device">{item.device}</span>
                    </li>
                  ))}
                </ul>
              )} */}
              <DownloadButtons
                page={page}
                store={store}
                className="hero-actions"
              />
            </div>
            {/* <MetaStrip>
              <MetaItem label="App Store 评分">
                {store.rating ? (
                  <>
                    <strong>{store.rating}</strong>
                    <Stars value={store.rating} />
                    <span className="count">
                      {formatRatingCount(store.ratingCount)} 个评分
                    </span>
                  </>
                ) : (
                  "暂无评分"
                )}
              </MetaItem>
              <MetaItem label="类别">{_.get(store, "genres[0]", "")}</MetaItem>
              <MetaItem label="年龄分级">{store.ageRating}</MetaItem>
              <MetaItem label="支持系统">{store.minimumOS}</MetaItem>
            </MetaStrip> */}
          </HeroMain>
        </HeroInner>
      </Hero>

      {_.size(store.screenshots) > 0 && (
        <Section>
          <SectionTitle>应用截图</SectionTitle>
          <Gallery>
            {_.map(store.screenshots, (url, idx) => (
              <img
                key={url}
                src={url}
                alt={`${appName} 应用截图 ${idx + 1}`}
                loading={idx === 0 ? "eager" : "lazy"}
                width="392"
                height="696"
              />
            ))}
          </Gallery>
        </Section>
      )}

      {_.size(paragraphs) > 0 && (
        <Section>
          <SectionTitle>应用简介</SectionTitle>
          <Description>
            {_.map(paragraphs, (line, idx) => (
              <p key={idx}>{line}</p>
            ))}
          </Description>
        </Section>
      )}

      {_.size(features) > 0 && (
        <Section>
          <SectionTitle>功能亮点</SectionTitle>
          <FeatureGrid>
            {_.map(features, (item) => (
              <Feature key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.info}</p>
              </Feature>
            ))}
          </FeatureGrid>
        </Section>
      )}
    </Page>
  );
}

const PAGE_WIDTH = 1020;

const Page = styled.div`
  width: 100%;
  background: #ffffff;
  color: #1d1d1f;
  padding: 0 0 72px 0;
`;

const Hero = styled.section`
  width: 100%;
  background: linear-gradient(180deg, #f5f7fb 0%, #ffffff 100%);
  border-bottom: 1px solid #ececf0;
`;

const HeroInner = styled.div`
  max-width: ${PAGE_WIDTH}px;
  margin: 0 auto;
  padding: 16px 21px 44px;
`;

const Crumb = styled.nav`
  ol {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 0 0 30px;
    padding: 0;
    list-style: none;
    font-size: 12px;
    color: #86868b;
  }
  li + li::before {
    content: "›";
    margin: 0 6px;
    color: #c7c7cc;
  }
  a {
    color: #6e6e73;
  }
  a:hover {
    color: #0066cc;
  }
`;

const HeroMain = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;

  > .icon {
    width: 110px;
    height: 110px;
    flex-shrink: 0;
    border-radius: 18px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
    object-fit: cover;
  }

  > .head {
    flex: 1;
    min-width: 0;
    margin-top: 0;

    > h1 {
      margin: 0;
      font-size: 24px;
      line-height: 32px;
      font-weight: 700;
      letter-spacing: -0.02em;
      color: #1d1d1f;
    }

    > .subtitle {
      margin: 6px 0 0;
      font-size: 14px;
      line-height: 21px;
      color: #515154;
    }

    > .developer {
      display: inline-block;
      margin-top: 8px;
      font-size: 14px;
      color: #0066cc;
    }

    > .awards {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin: 18px 0 0;
      padding: 0;
      list-style: none;
    }
  }

  ${breakpoint("desktop")`
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    align-items: start;
    gap: 24px;

    > .icon {
      width: 150px;
      height: 150px;
      border-radius: 32px;
    }

    > .head {
      margin-top: 0;

      > h1 {
        font-size: 30px;
        line-height: 38px;
      }

      > .subtitle {
        font-size: 16px;
        line-height: 24px;
      }
    }
  `}
`;

const MetaStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px 28px;
  margin: 26px 0 0;
  padding: 22px 0 0;
  border-top: 1px solid #e3e3e8;
  ${breakpoint("desktop")`
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 18px;
    margin: 0;
    padding: 4px 0 0 28px;
    border-top: 0;
    border-left: 1px solid #e3e3e8;
  `}
`;

const MetaItemWrapper = styled.div`
  > .label {
    display: block;
    font-size: 11px;
    color: #86868b;
  }
  > .value {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    font-size: 14px;
    font-weight: 600;
    color: #1d1d1f;
    > strong {
      font-size: 18px;
      line-height: 22px;
    }
    > .count {
      font-size: 12px;
      font-weight: 400;
      color: #86868b;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 22px;
  > a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 22px;
    border-radius: 20px;
    font-size: 15px;
    font-weight: 500;
    transition: all 0.2s ease;
  }
  > a.primary {
    background: #0071e3;
    color: #ffffff;
  }
  > a.primary:hover {
    background: #0077ed;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 113, 227, 0.28);
  }
  > a.secondary {
    border: 1px solid #d2d2d7;
    color: #0066cc;
    background: #ffffff;
  }
  > a.secondary:hover {
    border-color: #0071e3;
    background: #f5f9ff;
  }
  > .price {
    font-size: 13px;
    color: #6e6e73;
  }
`;

const Section = styled.section`
  max-width: ${PAGE_WIDTH}px;
  margin: 0 auto;
  padding: 44px 21px 0;
`;

const SectionTitle = styled.h2`
  position: relative;
  margin: 0 0 22px;
  padding-left: 14px;
  font-size: 22px;
  line-height: 28px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #1d1d1f;
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 4px;
    width: 4px;
    height: 20px;
    border-radius: 2px;
    background: #0071e3;
  }
`;

const Gallery = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding: 4px 2px 16px;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d8d8dd;
    border-radius: 3px;
  }
  > img {
    flex: 0 0 auto;
    width: 216px;
    height: 384px;
    border-radius: 16px;
    background: #f5f5f7;
    object-fit: cover;
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.1);
    ${breakpoint("desktop")`
      width: 252px;
      height: 448px;
      transition: transform 0.25s ease;
      &:hover {
        transform: translateY(-4px);
      }
    `}
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 32px;
  ${breakpoint("desktop")`
    grid-template-columns: 1fr 1fr;
    gap: 20px 24px;
  `}
`;

const Feature = styled.div`
  padding: 22px 24px;
  background: #fbfbfd;
  border: 1px solid #eeeef2;
  border-radius: 18px;
  > h3 {
    margin: 0;
    font-size: 16px;
    line-height: 22px;
    font-weight: 700;
    color: #1d1d1f;
  }
  > p {
    margin: 8px 0 0;
    font-size: 14px;
    line-height: 24px;
    color: #515154;
  }
  ${breakpoint("desktop")`
    transition: all 0.2s ease;
    &:hover {
      background: #ffffff;
      border-color: #dfe6f2;
      box-shadow: 0 8px 22px rgba(0, 0, 0, 0.06);
    }
  `}
`;

const Description = styled.div`
  /* max-width: 760px; */
  > p {
    margin: 0 0 12px;
    font-size: 15px;
    line-height: 27px;
    color: #333336;
  }
`;

const StarsRow = styled.div`
  display: flex;
  gap: 2px;
  > span {
    width: 13px;
    height: 13px;
    display: inline-block;
    background: #d2d2d7;
    clip-path: polygon(
      50% 0%,
      61% 35%,
      98% 35%,
      68% 57%,
      79% 91%,
      50% 70%,
      21% 91%,
      32% 57%,
      2% 35%,
      39% 35%
    );
  }
  > span.on {
    background: #ff9500;
  }
`;

const NotFound = styled.div`
  max-width: ${PAGE_WIDTH}px;
  margin: 0 auto;
  padding: 120px 21px;
  text-align: center;
  > h1 {
    margin: 0 0 12px;
    font-size: 26px;
  }
  > p {
    margin: 0;
    font-size: 15px;
    color: #6e6e73;
  }
`;
